import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PolicyViewer, PolicyType } from '../components/policies/PolicyViewer';

interface PolicyCard {
  type: PolicyType;
  title: { tr: string; en: string };
  description: { tr: string; en: string };
  icon: string;
  lastUpdated: string;
}

const PolicyIcon = ({ type }: { type: PolicyType }) => {
  const iconClass = "w-8 h-8";

  switch (type) {
    case 'terms_of_use':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    case 'privacy_policy':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    case 'cookie_policy':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'gdpr_compliance':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
        </svg>
      );
    case 'antifraud_policy':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
  }
};

const policyCards: PolicyCard[] = [
  {
    type: 'terms_of_use',
    title: { tr: 'Kullanım Şartları', en: 'Terms of Use' },
    description: {
      tr: 'Platform kullanım koşulları, kullanıcı yükümlülükleri ve hizmet tanımları',
      en: 'Platform usage conditions, user obligations and service definitions'
    },
    icon: 'gavel',
    lastUpdated: '2025-09-19',
  },
  {
    type: 'privacy_policy',
    title: { tr: 'Gizlilik Politikası', en: 'Privacy Policy' },
    description: {
      tr: 'Kişisel verilerinizin toplanması, işlenmesi ve korunması hakkında bilgiler',
      en: 'Information about the collection, processing and protection of your personal data'
    },
    icon: 'security',
    lastUpdated: '2025-09-19',
  },
  {
    type: 'cookie_policy',
    title: { tr: 'Çerez Politikası', en: 'Cookie Policy' },
    description: {
      tr: 'Web sitemizde çerezlerin nasıl kullanıldığı ve yönetildiği',
      en: 'How cookies are used and managed on our website'
    },
    icon: 'cookie',
    lastUpdated: '2025-09-19',
  },
  {
    type: 'gdpr_compliance',
    title: { tr: 'GDPR Uyum Politikası', en: 'GDPR Compliance Policy' },
    description: {
      tr: 'Avrupa Genel Veri Koruma Yönetmeliği (GDPR) uyumu',
      en: 'European General Data Protection Regulation (GDPR) compliance'
    },
    icon: 'shield',
    lastUpdated: '2025-09-19',
  },
  {
    type: 'antifraud_policy',
    title: { tr: 'Anti-Fraud Politikası', en: 'Anti-Fraud Policy' },
    description: {
      tr: 'Sahtecilik ve kötüye kullanıma karşı önlemler ve yaptırımlar',
      en: 'Measures and sanctions against fraud and abuse'
    },
    icon: 'description',
    lastUpdated: '2025-09-19',
  },
];

const Policies: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState<'tr' | 'en'>('tr');
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyType | null>(null);

  // Check URL params on mount
  useEffect(() => {
    const policyParam = searchParams.get('policy');
    if (policyParam) {
      // Validate if it's a valid policy type
      const validPolicies: PolicyType[] = ['terms_of_use', 'privacy_policy', 'cookie_policy', 'gdpr_compliance', 'antifraud_policy'];
      if (validPolicies.includes(policyParam as PolicyType)) {
        setSelectedPolicy(policyParam as PolicyType);
      }
    }
  }, [searchParams]);

  const handleLanguageChange = (newLanguage: 'tr' | 'en') => {
    setLanguage(newLanguage);
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center space-x-1 hover:text-indigo-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span>{language === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
        </button>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-gray-900">{language === 'tr' ? 'Politikalar' : 'Policies'}</span>
      </nav>

      {/* Header */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">
            {language === 'tr' ? 'Politikalar ve Sözleşmeler' : 'Policies and Agreements'}
          </h1>
          <div className="flex bg-white rounded-lg border border-gray-300">
            <button
              onClick={() => handleLanguageChange('tr')}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                language === 'tr'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              Türkçe
            </button>
            <button
              onClick={() => handleLanguageChange('en')}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                language === 'en'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              English
            </button>
          </div>
        </div>
        <p className="text-gray-600 leading-relaxed">
          {language === 'tr'
            ? 'PYRMD platformunu kullanırken geçerli olan tüm politikalar ve yasal metinler. Bu dokümanlar düzenli olarak güncellenmektedir.'
            : 'All policies and legal texts applicable when using the PYRMD platform. These documents are regularly updated.'}
        </p>
      </div>

      {/* Policy Cards or Selected Policy */}
      {selectedPolicy ? (
        <div>
          <button
            onClick={() => setSelectedPolicy(null)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {language === 'tr' ? 'Tüm Politikalar' : 'All Policies'}
          </button>
          <PolicyViewer
            policyType={selectedPolicy}
            language={language}
            onClose={() => setSelectedPolicy(null)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {policyCards.map((policy) => (
            <div
              key={policy.type}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 flex flex-col"
            >
              <div className="p-6 flex-1">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center bg-indigo-100 rounded-lg p-3 mr-4">
                    <div className="text-indigo-600">
                      <PolicyIcon type={policy.type} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {policy.title[language]}
                    </h3>
                    <span className="inline-block px-2 py-1 text-xs text-gray-600 bg-gray-100 border border-gray-300 rounded">
                      {language === 'tr' ? 'Güncelleme' : 'Updated'}: {policy.lastUpdated}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {policy.description[language]}
                </p>
              </div>
              <div className="p-6 pt-0">
                <button
                  onClick={() => setSelectedPolicy(policy.type)}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  {language === 'tr' ? 'Politikayı Görüntüle' : 'View Policy'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          {language === 'tr' ? 'Önemli Bilgi' : 'Important Information'}
        </h3>
        <p className="text-sm text-blue-800 leading-relaxed mb-4">
          {language === 'tr'
            ? 'Bu politikalar, PYRMD MÜZİK YAPIM PRODÜKSİYON SANAYİ VE TİCARET LİMİTED ŞİRKETİ tarafından hazırlanmıştır ve Türkiye Cumhuriyeti yasalarına tabidir. Politikalarımız düzenli olarak güncellenmektedir. Değişikliklerden haberdar olmak için bu sayfayı düzenli olarak ziyaret etmenizi öneririz.'
            : 'These policies have been prepared by PYRMD MÜZİK YAPIM PRODÜKSİYON SANAYİ VE TİCARET LİMİTED ŞİRKETİ and are subject to the laws of the Republic of Turkey. Our policies are regularly updated. We recommend visiting this page regularly to stay informed of changes.'}
        </p>
        <div className="text-xs text-blue-700 space-y-1">
          <div>
            <strong>{language === 'tr' ? 'İletişim' : 'Contact'}:</strong> general@pyrmdgroup.com
          </div>
          <div>
            <strong>{language === 'tr' ? 'Adres' : 'Address'}:</strong> Adalet Mah. Manas Blv. Folkart Towers 47 B / 2601 Bayraklı / İzmir / Türkiye
          </div>
        </div>
      </div>
    </div>
  );
};

export default Policies;